import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { ChartBar } from "@phosphor-icons/react";
import { RowanTerm } from "@/components/RowanTerm";

const label = { color: "var(--bk-text-2)" };
const money = (v) => `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;

function Bar({ value, max, color = "var(--bk-accent)" }) {
  const pct = max ? Math.max(2, Math.round((value / max) * 100)) : 0;
  return <div className="h-1.5 rounded-full overflow-hidden" style={{ background: "var(--bk-border)" }}><div className="h-full rounded-full" style={{ width: `${pct}%`, background: color }} /></div>;
}

export function RevenueInsights({ businessId }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get(`/businesses/${businessId}/wellness/insights`).then((r) => setData(r.data)).catch(() => setData({ error: true }));
  }, [businessId]);

  if (!data) return <p className="text-sm" style={label} data-testid="insights-loading">Loading…</p>;
  if (data.error) return <p className="text-sm" style={label} data-testid="insights-error">Couldn't load insights. Try again in a moment.</p>;

  const chips = [
    { k: "collected", label: "Money collected", note: "gross payments received, refunds included" },
    { k: "earned", label: "Earned", note: "sessions delivered + gift cards redeemed" },
    { k: "unearned", label: "Prepaid / unearned", note: "sessions & gift-card balance still owed" },
    { k: "refunded", label: "Refunded", note: "returned to clients (part of collected)" },
    { k: "forfeited", label: "Forfeited / expired", note: "unused value kept under policy" },
    { k: "discounts", label: "Discounts given", note: "vs. list price · not part of the bridge" },
  ];
  const t = data.totals;
  const bridgeParts = [["earned", "earned"], ["unearned", "prepaid"], ["refunded", "refunded"], ["forfeited", "forfeited"]]
    .filter(([k]) => k !== "forfeited" || t.forfeited > 0)
    .map(([k, w]) => `${money(t[k])} ${w}`);
  const maxSvc = Math.max(...data.by_service.map((s) => s.collected), 1);
  const maxDay = Math.max(...data.by_weekday.map((d) => d.value), 1);
  const maxHr = Math.max(...data.by_hour.map((h) => h.value), 1);

  return (
    <div className="space-y-6" data-testid="revenue-insights">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="insights-totals">
        {chips.map((c) => (
          <div key={c.k} className="bk-card p-4" data-testid={`insights-chip-${c.k}`}>
            <p className="text-xs" style={label}>{c.k === "unearned" ? <RowanTerm term="unearned_revenue" size={12}>{c.label}</RowanTerm> : c.k === "earned" ? <RowanTerm term="cash_vs_earned" size={12}>{c.label}</RowanTerm> : c.k === "refunded" ? <RowanTerm term="refund_vs_chargeback" size={12}>{c.label}</RowanTerm> : c.label}</p>
            <p className="font-display text-xl font-bold mt-1" style={{ color: c.k === "refunded" ? "#B23B1F" : "var(--bk-text)" }}>{money(data.totals[c.k])}</p>
            <p className="text-[10px] mt-0.5" style={label}>{c.note}</p>
          </div>
        ))}
      </div>
      {t.collected > 0 && (
        <p className="text-xs font-mono leading-relaxed" style={label} data-testid="insights-bridge">
          Reconciliation: {money(t.collected)} collected = {bridgeParts.join(" + ")}
          {" "}({t.reconciles ? "reconciles exactly" : `off by ${money(Math.abs(t.collected - t.bridge_total))}`}) · net of refunds {money(t.net_collected)}
        </p>
      )}
      {data.ledger && t.collected > 0 && (
        <div className="bk-card p-4 text-xs leading-relaxed" data-testid="insights-ledger-tieout">
          <p className="font-semibold mb-1" style={{ color: data.ledger.all_tie ? "var(--bk-success)" : "#B23B1F" }} data-testid="insights-ledger-status">
            {data.ledger.all_tie ? "Ties to your books" : "Does not tie to your books — Rowan will re-post on the next refresh"} · {data.ledger.posting_count} ledger entries
          </p>
          <p className="font-mono" style={label}>
            Transactions: {money(data.ledger.cash_in + (data.ledger.pay_per_session || 0))} cash in − {money(data.ledger.refunds_cash)} refunds = {money(data.ledger.net_cash + (data.ledger.pay_per_session || 0))} net cash (Bank)
            {" · "}Balance sheet: Deferred Revenue {money(data.ledger.deferred_balance)} = prepaid still owed
            {" · "}P&amp;L: {money(data.ledger.recognized + (data.ledger.pay_per_session || 0))} earned + {money(data.ledger.forfeited)} forfeited
          </p>
        </div>
      )}
      <p className="text-sm italic leading-relaxed max-w-3xl" style={label} data-testid="insights-rowan-summary">Rowan: “{data.rowan_summary}”</p>

      <div className="bk-card p-5" data-testid="insights-by-service">
        <h3 className="text-xs uppercase tracking-[0.2em] font-semibold mb-3 inline-flex items-center gap-1.5" style={{ color: "var(--bk-accent)" }}><ChartBar size={14} weight="fill" /> By service &amp; package (collected money)</h3>
        <div className="space-y-3">
          {data.by_service.length === 0 && <p className="text-sm" style={label}>Nothing sold yet.</p>}
          {data.by_service.map((s) => (
            <div key={s.name} data-testid={`insights-service-${s.name}`}>
              <div className="flex flex-wrap justify-between gap-2 text-sm mb-1">
                <span className="font-semibold" style={{ color: "var(--bk-text)" }}>{s.name}</span>
                <span style={label}>
                  {money(s.collected)} collected · {money(s.earned)} earned · {money(s.unearned)} unearned
                  {s.refunded > 0 ? ` · ${money(s.refunded)} refunded` : ""}{s.forfeited > 0 ? ` · ${money(s.forfeited)} forfeited` : ""}{s.discounts > 0 ? ` · ${money(s.discounts)} discounts` : ""}
                </span>
              </div>
              <Bar value={s.collected} max={maxSvc} />
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bk-card p-5" data-testid="insights-by-day">
          <h3 className="text-xs uppercase tracking-[0.2em] font-semibold mb-3" style={{ color: "var(--bk-accent)" }}>By day of week</h3>
          <div className="space-y-2.5">
            {data.by_weekday.map((d) => (
              <div key={d.day}>
                <div className="flex justify-between text-xs mb-0.5"><span className="font-semibold" style={{ color: "var(--bk-text)" }}>{d.day}</span><span style={label}>{d.sessions} sessions · {money(d.value)}</span></div>
                <Bar value={d.value} max={maxDay} color="var(--bk-success)" />
              </div>
            ))}
          </div>
        </div>
        <div className="bk-card p-5" data-testid="insights-by-hour">
          <h3 className="text-xs uppercase tracking-[0.2em] font-semibold mb-3" style={{ color: "var(--bk-accent)" }}>By time slot</h3>
          <div className="space-y-2.5">
            {data.by_hour.length === 0 && <p className="text-sm" style={label}>No completed sessions with times yet.</p>}
            {data.by_hour.map((h) => (
              <div key={h.hour}>
                <div className="flex justify-between text-xs mb-0.5"><span className="font-semibold" style={{ color: "var(--bk-text)" }}>{h.hour}</span><span style={label}>{h.sessions} sessions · {money(h.value)}</span></div>
                <Bar value={h.value} max={maxHr} color="#B08840" />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
